import React, {ChangeEvent, useState} from 'react';
import Input from "./Input";
import {DateTime} from "luxon";

// Types ----

type DateRange = {
  start: string
  end: string
  expression: string
}

type DateRangePickerProps = {
  name: string
  value?: Partial<DateRange> | string | null
  onChange: (value: DateRange) => void
  startMin?: string
  startMax?: string
  endMax?: string
}

// Helpers ----

function parseDate(date: string): DateTime {
  const today = DateTime.now().startOf('day');

  if (date.split('-', 3).length < 3) {
    date = today.year + '-' + date;
  }

  const dateTime = DateTime.fromISO(date);

  // If valid date format it for the input
  if (!dateTime.isValid) return DateTime.fromISO('');

  return DateTime.fromISO(date);
}

function parseDateRange(value?: Partial<DateRange> | string | null): DateRange {
  const today = DateTime.now().startOf('day');
  const currentDate = today.toISODate();
  const dateRange: DateRange = {start: currentDate, end: currentDate, expression: `${currentDate} to ${currentDate}`}

  if (!value) {
    return dateRange
  }

  let start, end;

  if (typeof value === 'string') {
    [start, end] = value.split(' to ', 2)
  } else {
    start = value.start ?? dateRange.start;
    end = value.end ?? dateRange.end;
  }

  let startDate = start ? parseDate(start) : null;
  let endDate = end ? parseDate(end) : null;

  dateRange.start = startDate?.toISODate() ?? '';
  dateRange.end = endDate?.toISODate() ?? '';
  dateRange.expression = [dateRange.start, dateRange.end].join(' to ')

  return dateRange
}

function toRelativeRange(range: DateRange): DateRange {
  const newRange = {...range};

  newRange.start = newRange.start.split('-').slice(1).join('-');
  newRange.end = newRange.end.split('-').slice(1).join('-');
  newRange.expression = `${newRange.start} to ${newRange.end}`;

  return newRange;
}

// Component ----

function DateRangePicker({name, value, onChange, startMin, startMax, endMax}: DateRangePickerProps) {

  const [range, setRange] = useState<DateRange>(parseDateRange(value));

  function handleChangeStart({currentTarget}: ChangeEvent<HTMLInputElement>) {
    const newStart = currentTarget.value
    const newRange = {start: newStart, end: range.end, expression: [newStart, range.end].join(' to ')}

    setRange(newRange)
    onChange(toRelativeRange(newRange))
  }

  function handleChangeEnd({currentTarget}: ChangeEvent<HTMLInputElement>) {
    const newEnd = currentTarget.value
    const newRange = {start: range.start, end: newEnd, expression: [range.start, newEnd].join(' to ')}

    setRange(newRange)
    onChange(toRelativeRange(newRange))
  }

  return (
    <div className="flex space-x-2 items-center">
      <Input
        name={`${name}-start`}
        type="date"
        value={range.start}
        onChange={handleChangeStart}
        required
        min={startMin}
        max={startMax}
      />

      <span> to </span>

      <Input
        name={`${name}-end`}
        type="date"
        value={range.end}
        onChange={handleChangeEnd}
        required
        min={range.start}
        max={endMax}
      />
    </div>
  );
}

// Export ----

export default DateRangePicker;